import axios from "axios";
import { ApiResponse } from "@/types";
import { logger } from "@/services/LoggerService";

export interface Award {
    id: string;
    trophy_name: string;
    created_at: string;
    isViewed: boolean;
    userId: string;
    type: string;
}

export interface GetAwardsParams {
    type: string;
    userId: string;
    isViewed: boolean;
}

export interface UpdateAwardParams {
    awardId: string;
    isViewed: boolean;
}

/**
 * Fetches awards for a user based on specified criteria.
 *
 * @param options - The options for fetching awards.
 * @param options.type - The type filter for awards (e.g., "daily", "weekly").
 * @param options.userId - The ID of the user for whom to fetch awards.
 * @param options.isViewed - Filter for viewed/unviewed awards.
 *
 * @returns A promise that resolves to an array of awards.
 */
export const getAwards = async ({
    type,
    userId,
    isViewed,
}: GetAwardsParams): Promise<ApiResponse<Award[]> | undefined> => {
    try {
        const query = new URLSearchParams({
            type,
            isViewed: isViewed.toString(),
        }).toString();

        const awards = await axios.get(`/api/user-trophies/users/${userId}/history?${query}`);
        return awards.data;
    } catch (error) {
        console.log(error);
        logger.error("Failed to fetch awards", error, {
            type,
            userId,
            isViewed,
            context: "AwardService",
        });
        return undefined;
    }
};

/**
 * Update the viewed status of a specific award.
 *
 * @param params - The parameters for the award update.
 * @param params.awardId - The unique identifier of the award to be updated.
 * @param params.isViewed - The new viewed status to be set for the award.
 *
 * @returns A promise that resolves with the updated award data.
 */
export const updateAward = async ({
    awardId,
    isViewed,
}: UpdateAwardParams): Promise<ApiResponse<Award> | undefined> => {
    try {
        const award = await axios.patch(`/api/user-trophies/${awardId}`, { isViewed });
        return award.data;
    } catch (error) {
        console.log(error);
        logger.error("Failed to update award", error, {
            awardId,
            isViewed,
            context: "AwardService",
        });
        return undefined;
    }
};
